import { EarningsReportingPeriod } from '@api/api/earnings';
import { FilterResults } from '@api/api/screener';
import { LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD } from '@terminal/views/screener/results/constants';
import { PulseCompanyRef } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';

export const PULSE_BIG_MOVERS_PER_SIDE = 3;

// Names and tickers are resolved by PulseCompanyCell via useEntity.
export const toCompanyRef = (entityTag: string): PulseCompanyRef => ({
  entityTag,
  name: '',
  ticker: '',
});

/** Per-entity 1D % change; rows in each screener group align with `entities`. */
export const extractOneDayReturns = (
  results: FilterResults
): [string, number][] => {
  const group = results.data.find(
    g => g.data[0]?.[LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD] !== undefined
  );

  return results.entities.flatMap((tag, index) => {
    const value =
      group?.data[index]?.[LIVE_ONE_DAY_RETURN_COLUMN_META_VALUE_FIELD]?.value;
    return value === undefined ? [] : [[tag, value]];
  });
};

export const pickTopMovers = (
  returns: [string, number][]
): [string, number][] => {
  const winners = returns
    .filter(([, change]) => change > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, PULSE_BIG_MOVERS_PER_SIDE);
  const losers = returns
    .filter(([, change]) => change < 0)
    .sort((a, b) => a[1] - b[1])
    .slice(0, PULSE_BIG_MOVERS_PER_SIDE);

  return [...winners, ...losers];
};

export type PulseEarningsStatusKey =
  | 'notScheduledYet'
  | 'remainingDatesUnannounced'
  | 'reportingComplete';

/**
 * Why a theme basket has no upcoming earnings to show this calendar quarter.
 * Only meaningful once the section is empty — when rows exist they are the
 * answer, so callers should not ask.
 *
 * `period` is this calendar quarter's row from earnings/v2/filter/periods
 * (grouped by reporting_date). `total` is distinct entities with an earnings
 * row; `reported` is how many of those have reported=true.
 */
export const getPulseEarningsStatus = (
  period?: EarningsReportingPeriod
): { key: PulseEarningsStatusKey } => {
  // Handle no companies have reported and no dates are announced
  if (!period || period.total === 0) {
    return { key: 'notScheduledYet' };
  }

  const { total, reported } = period;

  if (reported < total) {
    return { key: 'remainingDatesUnannounced' };
  }

  return { key: 'reportingComplete' };
};
